import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ExternalLink,
  Maximize2,
  Minimize2,
  Download,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@roger/ui-components';
import { Document, Page, pdfjs } from 'react-pdf';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { PdfPageErrorBoundary } from './PdfPageErrorBoundary';

// Bundle the worker locally (no CDN/CORS dependency). The direct pdfjs-dist
// pin in frontend/package.json must stay in lockstep with react-pdf's exact
// pdfjs-dist requirement, or the API and worker versions diverge at runtime.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface PDFViewerProps {
  url: string | null;
  loading?: boolean;
  error?: string | null;
  onDownload?: () => void;
  onOpenInNewTab?: () => void;
  className?: string;
  containerClassName?: string;
  showControls?: boolean;
  showPageChanger?: boolean;
  allowHandPan?: boolean;
  maxWidth?: number;
  maxHeight?: number;
  defaultScale?: number;
  minScale?: number;
  maxScale?: number;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  url,
  loading = false,
  error = null,
  onDownload,
  onOpenInNewTab,
  className = '',
  containerClassName = '',
  showControls = true,
  showPageChanger = true,
  allowHandPan = true,
  maxWidth = 800,
  defaultScale = 1.0,
  minScale = 0.5,
  maxScale = 3.0,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [documentLoadCount, setDocumentLoadCount] = useState(0);
  const [scale, setScale] = useState(defaultScale);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Pan state
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  // Measure container width
  const updateContainerWidth = useCallback(() => {
    if (pdfContainerRef.current) {
      const width = pdfContainerRef.current.getBoundingClientRect().width;
      setContainerWidth(width - 32); // Subtract padding
    }
  }, []);

  // Set up resize listener
  useEffect(() => {
    updateContainerWidth();
    const handleResize = () => updateContainerWidth();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateContainerWidth]);

  // Reset scale and pan when URL changes
  useEffect(() => {
    setScale(defaultScale);
    setCurrentPage(1);
    setPan({ x: 0, y: 0 });
  }, [url, defaultScale]);

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
    setDocumentLoadCount(count => count + 1);
  };

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, maxScale));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, minScale));
  };

  const handleResetZoom = () => {
    setScale(defaultScale);
  };

  const handleFullscreenToggle = () => {
    setIsFullscreen(prev => !prev);
  };

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!url) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          handlePreviousPage();
          break;
        case 'ArrowRight':
          event.preventDefault();
          handleNextPage();
          break;
        case '+':
        case '=':
          event.preventDefault();
          handleZoomIn();
          break;
        case '-':
          event.preventDefault();
          handleZoomOut();
          break;
        case '0':
          event.preventDefault();
          handleResetZoom();
          break;
        case 'f':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setIsFullscreen(prev => !prev);
          }
          break;
        case 'Escape':
          if (isFullscreen) {
            event.preventDefault();
            setIsFullscreen(false);
          }
          break;
      }
    },
    [url, isFullscreen]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Pan functionality
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!url || !allowHandPan) return;

      // Enable dragging at all zoom levels
      setIsDragging(true);
      setDragStart({
        x: event.clientX - pan.x,
        y: event.clientY - pan.y,
      });
    },
    [url, pan, allowHandPan]
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging || !url) return;

      setPan({
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y,
      });
    },
    [isDragging, url, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Reset pan when URL changes (but not when scale changes)
  // Removed the scale-based pan reset to allow panning at all zoom levels

  // Mouse wheel zoom
  const handleWheel = useCallback(
    (event: Event) => {
      if (!url) return;

      const wheelEvent = event as WheelEvent;
      if (wheelEvent.ctrlKey || wheelEvent.metaKey) {
        event.preventDefault();
        const delta = wheelEvent.deltaY > 0 ? -0.1 : 0.1;
        setScale(prev => Math.max(minScale, Math.min(maxScale, prev + delta)));
      }
    },
    [url, minScale, maxScale]
  );

  useEffect(() => {
    const pdfContainer = pdfContainerRef.current;
    if (pdfContainer) {
      pdfContainer.addEventListener('wheel', handleWheel, { passive: false });
      return () => pdfContainer.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4'></div>
          <p className='text-muted-foreground'>Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className='text-center'>
          <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <p className='text-destructive mb-4'>{error}</p>
          {onDownload && (
            <Button onClick={onDownload} variant='outline'>
              <Download className='h-4 w-4' />
              Download Instead
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!url) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className='text-center'>
          <p className='text-muted-foreground'>No PDF available</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border rounded-lg overflow-hidden bg-white shadow-sm pdf-viewer-container ${
        isFullscreen ? 'fixed inset-0 z-50 bg-white' : ''
      } ${containerClassName}`}
    >
      {/* Controls Header */}
      {showControls && (
        <div className='p-3 border-b border-muted bg-gray-50 flex items-center justify-between'>
          {/* Left: Page Navigation */}
          {showPageChanger && totalPages > 1 ? (
            <div className='flex justify-between items-center'>
              <Button
                variant='outline'
                size='sm'
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className='flex items-center'
                title='Previous Page (←)'
              >
                <ChevronLeft className='h-4 w-4' />
              </Button>
              <div className='text-sm text-muted-foreground font-medium mx-4'>
                Page {currentPage} of {totalPages}
              </div>
              <Button
                variant='outline'
                size='sm'
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className='flex items-center'
                title='Next Page (→)'
              >
                <ChevronRight className='h-4 w-4 ml-1' />
              </Button>
            </div>
          ) : (
            <div></div>
          )}

          {/* Right: Zoom and Other Controls */}
          <div className='flex items-center gap-1'>
            <Button
              variant='outline'
              size='sm'
              onClick={handleZoomOut}
              disabled={scale <= minScale}
              title='Zoom Out (Ctrl/Cmd + -)'
            >
              <ZoomOut className='h-3 w-3' />
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={handleResetZoom}
              disabled={scale === defaultScale}
              title='Reset Zoom (0)'
            >
              <RotateCcw className='h-3 w-3' />
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={handleZoomIn}
              disabled={scale >= maxScale}
              title='Zoom In (Ctrl/Cmd + +)'
            >
              <ZoomIn className='h-3 w-3' />
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={handleFullscreenToggle}
              title='Toggle Fullscreen (Ctrl/Cmd + F)'
            >
              {isFullscreen ? (
                <Minimize2 className='h-3 w-3' />
              ) : (
                <Maximize2 className='h-3 w-3' />
              )}
            </Button>
            {onOpenInNewTab && (
              <Button
                variant='outline'
                size='sm'
                onClick={onOpenInNewTab}
                title='Open in New Tab'
              >
                <ExternalLink className='h-3 w-3' />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* PDF Content */}
      <div
        className={`h-full w-full overflow-auto flex flex-col ${isFullscreen ? 'flex-1' : containerClassName?.includes('h-full') ? 'flex-1' : 'h-[800px]'}`}
      >
        <div
          ref={pdfContainerRef}
          className='flex-1 min-h-0 relative'
          style={{
            cursor: allowHandPan
              ? isDragging
                ? 'grabbing'
                : 'grab'
              : 'default',
          }}
          onMouseDown={handleMouseDown}
        >
          <div
            className='transition-transform duration-100 ease-out flex flex-col items-center gap-4'
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px)`,
            }}
          >
            <Document
              file={url}
              loading={
                <div className='p-4 text-center'>
                  <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2'></div>
                  <span className='text-sm text-muted-foreground'>
                    Loading PDF...
                  </span>
                </div>
              }
              error={
                <div className='p-4 text-center text-destructive'>
                  <AlertTriangle className='h-8 w-8 mx-auto mb-2' />
                  <span className='block'>Failed to load PDF</span>
                </div>
              }
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={() => console.error('Failed to load PDF')}
            >
              {totalPages &&
                containerWidth &&
                Array.from({ length: totalPages }, (_, index) => index + 1).map(
                  pageNumber => (
                    <PdfPageErrorBoundary
                      key={pageNumber}
                      resetKey={documentLoadCount}
                      fallback={<div aria-hidden='true' className='h-24' />}
                      errorFallback={
                        <div className='p-4 text-center text-xs text-muted-foreground'>
                          Failed to load page
                        </div>
                      }
                    >
                      <Page
                        width={Math.min(containerWidth, maxWidth) * scale}
                        pageNumber={pageNumber}
                        loading={
                          <div className='p-4 text-center'>
                            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary mx-auto mb-2'></div>
                            <span className='text-xs text-muted-foreground'>
                              Loading page {pageNumber}...
                            </span>
                          </div>
                        }
                      />
                    </PdfPageErrorBoundary>
                  )
                )}
            </Document>
          </div>
        </div>

        {/* Viewer Footer */}
        <div className='flex-0 p-4 border-t border-border bg-muted/50'>
          <div className='flex justify-between items-center'></div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
